import AddIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import ReactComponent from '../../ReactComponent';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Typography
} from '@mui/material';
import ModalConfirmacao, { TIPO_CONFIRMACAO } from '@/components/sessao/ModalConfirmacao';
import { red } from '@mui/material/colors';

const TAMANHO_MAXIMO_CAMPO_CODIGO = 30;
const TAMANHO_MAXIMO_CAMPO_CONTEUDO = 400;

class AutoTextoForm extends ReactComponent {
    tipo = this.observableProps.tipo ?? 'cadastrar';
    id = this.observableProps.id ?? '';
    codigo = this.observableProps.codigo ?? '';
    conteudo = this.observableProps.conteudo ?? '';
    camposInvalidos = [];

    constructor(props) {
        super(props);

        makeObservable(this, {
            tipo: observable,
            id: observable,
            codigo: observable,
            conteudo: observable,
            camposInvalidos: observable,
            handleChangeCodigo: action,
            handleChangeConteudo: action,
            validarFormulario: action,
            handleInclusaoDecPad: action,
            handleEdicaoDecPad: action,
            handleExclusaoDecPad: action,
            acoes: computed,
            acaoAtual: computed,
            campoCodigoEstaInvalido: computed,
            campoConteudoEstaInvalido: computed
        });
    }

    handleChangeCodigo = (event) => {
        const novoValor = event.target.value;
        if (novoValor.length > 0 && this.campoCodigoEstaInvalido) {
            this.camposInvalidos = this.camposInvalidos.filter((campoInvalido) => campoInvalido !== 'codigo');
        }
        if (novoValor.length <= TAMANHO_MAXIMO_CAMPO_CODIGO) {
            this.codigo = novoValor.toUpperCase();
        }
    };

    handleChangeConteudo = (event) => {
        const novoValor = event.target.value;
        if (novoValor.length > 0 && this.campoConteudoEstaInvalido) {
            this.camposInvalidos = this.camposInvalidos.filter((campoInvalido) => campoInvalido !== 'conteudo');
        }
        if (novoValor.length <= TAMANHO_MAXIMO_CAMPO_CONTEUDO) {
            this.conteudo = novoValor;
        }
    };

    validarFormulario = () => {
        let formularioValido = true;
        if (this.codigo.trim().length === 0) {
            formularioValido = false;
            this.camposInvalidos.push('codigo');
        }
        if (this.conteudo.trim().length === 0) {
            formularioValido = false;
            this.camposInvalidos.push('conteudo');
        }
        return formularioValido;
    };

    handleInclusaoDecPad = async () => {
        if (this.validarFormulario()) {
            await this.sessaoService.cadastrarAutoTexto({
                siglaOrgaoJudicante: this.usuarioStore.usuario.localizacao,
                nomeAutoTexto: this.codigo,
                autoTexto: this.conteudo
            });
            this.mensagemStore.abrirMensagemSucesso('Nova opção do Dec-Pad cadastrada com sucesso');
            this.observableProps.handleCloseDialog();
        }
    };

    handleEdicaoDecPad = async () => {
        if (this.validarFormulario()) {
            await this.sessaoService.editarAutoTexto({
                id: this.id,
                siglaOrgaoJudicante: this.usuarioStore.usuario.localizacao,
                nomeAutoTexto: this.codigo,
                autoTexto: this.conteudo
            });
            this.mensagemStore.abrirMensagemSucesso(`Opção ${this.codigo} do Dec-Pad alterada com sucesso`);
            this.observableProps.handleCloseDialog();
        }
    };

    handleExclusaoDecPad = async () => {
        await this.sessaoService.removerAutoTexto({
            id: this.id,
            siglaOrgaoJudicante: this.usuarioStore.usuario.localizacao
        });
        this.mensagemStore.abrirMensagemSucesso(`Opção ${this.codigo} do Dec-Pad removida com sucesso`);
        this.observableProps.handleCloseDialog();
    };

    get acoes() {
        return {
            cadastrar: {
                titulo: 'Cadatrar nova opção no Dec-Pad',
                textoBotao: 'Cadastrar',
                icone: <AddIcon />,
                acao: this.handleInclusaoDecPad
            },
            editar: {
                titulo: 'Editar opção do Dec-Pad',
                textoBotao: 'Editar',
                icone: <EditIcon />,
                acao: this.handleEdicaoDecPad
            },
            excluir: {
                acao: this.handleExclusaoDecPad
            }
        };
    }

    get acaoAtual() {
        return this.acoes[this.tipo];
    }

    get campoCodigoEstaInvalido() {
        return this.camposInvalidos.includes('codigo');
    }

    get campoConteudoEstaInvalido() {
        return this.camposInvalidos.includes('conteudo');
    }

    render() {
        return this.tipo === 'excluir' ? (
            <ModalConfirmacao
                open={true}
                key="confirmacao-exclusao-dec-pad"
                titulo="Exclusão de opção do Dec-Pad"
                tipoConfirmacao={TIPO_CONFIRMACAO.EXCLUSAO}
                callbackCancelar={this.observableProps.handleCloseDialog}
                callbackConfirmar={this.acaoAtual.acao}
            >
                <Typography display={'inline'}>
                    Deseja realmente excluir a opção <strong>{this.observableProps.codigo}</strong> do Dec-Pad?
                </Typography>
            </ModalConfirmacao>
        ) : (
            <Dialog
                open={true}
                onClose={this.observableProps.handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="lg"
            >
                <DialogTitle id="alert-dialog-title">{this.acaoAtual.titulo}</DialogTitle>
                <DialogContent sx={{ minWidth: '600px' }}>
                    <InputLabel
                        htmlFor="codigo"
                        sx={{ mb: 0.5 }}
                    >
                        Código
                    </InputLabel>
                    <OutlinedInput
                        id="codigo"
                        size="small"
                        fullWidth
                        onChange={this.handleChangeCodigo}
                        value={this.codigo}
                        error={this.campoCodigoEstaInvalido}
                    />
                    {this.campoCodigoEstaInvalido && (
                        <FormHelperText sx={{ color: red[800] }}>Campo obrigatório.</FormHelperText>
                    )}
                    <FormHelperText>Deve ter no máximo {TAMANHO_MAXIMO_CAMPO_CODIGO} caracteres.</FormHelperText>

                    <InputLabel
                        htmlFor="conteudo"
                        sx={{ mt: 2, mb: 0.5 }}
                    >
                        Conteúdo
                    </InputLabel>
                    <OutlinedInput
                        id="conteudo"
                        size="small"
                        fullWidth
                        minRows={5}
                        multiline
                        onChange={this.handleChangeConteudo}
                        value={this.conteudo}
                        error={this.camposInvalidos.includes('conteudo')}
                    />
                    {this.campoConteudoEstaInvalido && (
                        <FormHelperText sx={{ color: red[800] }}>Campo obrigatório.</FormHelperText>
                    )}
                    <FormHelperText>Deve ter no máximo {TAMANHO_MAXIMO_CAMPO_CONTEUDO} caracteres.</FormHelperText>
                </DialogContent>
                <DialogActions sx={{ mx: 2, mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        color="secondary"
                        onClick={this.observableProps.handleCloseDialog}
                        variant="outlined"
                        data-testid="closeDialog"
                        endIcon={<CancelIcon />}
                    >
                        Fechar
                    </Button>
                    <Button
                        onClick={this.acaoAtual.acao}
                        variant="contained"
                        endIcon={this.acaoAtual.icone}
                    >
                        {this.acaoAtual.textoBotao}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default inject('stores')(observer(AutoTextoForm));
